import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Car, FileText, Calendar, Shield } from 'lucide-react';
import type { Vehicle, VehicleForm, ModalState } from '../types/system-parameters.types';

interface VehicleModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: VehicleForm, isEdit?: boolean, itemId?: string) => void;
}

const brandOptions = [
  'Honda', 'Yamaha', 'Suzuki', 'Toyota', 'Nissan', 'Hyundai', 
  'Chevrolet', 'Kia', 'Ford', 'Volkswagen', 'Bajaj', 'TVS'
];

const colorOptions = [
  'Blanco', 'Negro', 'Gris', 'Azul', 'Rojo', 'Verde', 
  'Amarillo', 'Plateado', 'Dorado', 'Marrón', 'Naranja'
];

export const VehicleModal: React.FC<VehicleModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<VehicleForm>({
    plate: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    engineNumber: '',
    chassisNumber: '',
    registrationExpiry: '',
    insuranceExpiry: '',
    technicalReviewExpiry: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const vehicle = modalState.item as Vehicle;
        setFormData({
          plate: vehicle.additionalData.plate,
          brand: vehicle.additionalData.brand,
          model: vehicle.additionalData.model,
          year: vehicle.additionalData.year,
          color: vehicle.additionalData.color || '',
          engineNumber: vehicle.additionalData.engineNumber || '',
          chassisNumber: vehicle.additionalData.chassisNumber || '',
          registrationExpiry: vehicle.additionalData.registrationExpiry || '',
          insuranceExpiry: vehicle.additionalData.insuranceExpiry || '',
          technicalReviewExpiry: vehicle.additionalData.technicalReviewExpiry || ''
        });
      } else {
        // Resetear para modo crear
        setFormData({
          plate: '',
          brand: '',
          model: '',
          year: new Date().getFullYear(),
          color: '',
          engineNumber: '',
          chassisNumber: '',
          registrationExpiry: '',
          insuranceExpiry: '',
          technicalReviewExpiry: ''
        });
      }
      setErrors({});
    }
  }, [modalState]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validaciones obligatorias
    if (!formData.plate.trim()) {
      newErrors.plate = 'La placa es obligatoria';
    } else if (!/^[A-Z0-9]{3}-[0-9]{3}$|^[A-Z][0-9][A-Z]-[0-9]{3}$/.test(formData.plate.trim())) {
      newErrors.plate = 'Formato inválido (ej: ABC-123 o A1B-234)';
    }

    if (!formData.brand.trim()) {
      newErrors.brand = 'La marca es obligatoria';
    }

    if (!formData.model.trim()) {
      newErrors.model = 'El modelo es obligatorio';
    }

    if (!formData.year || formData.year < 1990 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = `El año debe estar entre 1990 y ${new Date().getFullYear() + 1}`;
    }

    // Validaciones de fechas (si se proporcionan)
    const today = new Date();
    
    if (formData.registrationExpiry) {
      const regDate = new Date(formData.registrationExpiry);
      if (regDate < today) {
        newErrors.registrationExpiry = 'La fecha de registro no puede estar vencida';
      }
    }

    if (formData.insuranceExpiry) {
      const insDate = new Date(formData.insuranceExpiry);
      if (insDate < today) {
        newErrors.insuranceExpiry = 'La fecha del SOAT no puede estar vencida';
      }
    }

    if (formData.technicalReviewExpiry) {
      const techDate = new Date(formData.technicalReviewExpiry);
      if (techDate < today) {
        newErrors.technicalReviewExpiry = 'La fecha de revisión técnica no puede estar vencida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const isEdit = modalState.mode === 'edit';
      const itemId = modalState.item?.id;
      
      onSave(formData, isEdit, itemId);
      onClose();
    } catch (error) {
      console.error('Error al guardar vehículo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof VehicleForm, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="h-5 w-5 text-macartur-primary" />
            {modalState.mode === 'edit' ? 'Editar Vehículo' : 'Agregar Nuevo Vehículo'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Básica del Vehículo */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Car className="h-4 w-4" />
              Información del Vehículo
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plate">
                  Placa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="plate"
                  value={formData.plate}
                  onChange={(e) => handleInputChange('plate', e.target.value.toUpperCase())}
                  placeholder="ABC-123"
                  className={errors.plate ? 'border-red-500' : ''}
                  maxLength={7}
                />
                {errors.plate && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.plate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="brand">
                  Marca <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.brand} 
                  onValueChange={(value) => handleInputChange('brand', value)}
                >
                  <SelectTrigger className={errors.brand ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Seleccionar marca" />
                  </SelectTrigger>
                  <SelectContent>
                    {brandOptions.map((brand) => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.brand && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.brand}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">
                  Modelo <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  placeholder="CB 125F"
                  className={errors.model ? 'border-red-500' : ''}
                />
                {errors.model && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.model}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="year">
                  Año <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="year"
                  type="number"
                  min="1990"
                  max={new Date().getFullYear() + 1}
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', parseInt(e.target.value) || new Date().getFullYear())}
                  className={errors.year ? 'border-red-500' : ''}
                />
                {errors.year && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.year}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="color">Color</Label>
                <Select 
                  value={formData.color} 
                  onValueChange={(value) => handleInputChange('color', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar color" />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((color) => (
                      <SelectItem key={color} value={color}>
                        {color}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Información Técnica */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Información Técnica (Opcional)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="engineNumber">Número de Motor</Label>
                <Input
                  id="engineNumber"
                  value={formData.engineNumber}
                  onChange={(e) => handleInputChange('engineNumber', e.target.value.toUpperCase())}
                  placeholder="CB125F23001234"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="chassisNumber">Número de Chasis</Label>
                <Input
                  id="chassisNumber"
                  value={formData.chassisNumber}
                  onChange={(e) => handleInputChange('chassisNumber', e.target.value.toUpperCase())}
                  placeholder="MC52CB125F23001234"
                />
              </div>
            </div>
          </div>

          {/* Documentación */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Documentación
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="registrationExpiry">Vencimiento Tarjeta</Label>
                <Input
                  id="registrationExpiry"
                  type="date"
                  value={formData.registrationExpiry}
                  onChange={(e) => handleInputChange('registrationExpiry', e.target.value)}
                  className={errors.registrationExpiry ? 'border-red-500' : ''}
                />
                {errors.registrationExpiry && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.registrationExpiry}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="insuranceExpiry">Vencimiento SOAT</Label>
                <Input
                  id="insuranceExpiry"
                  type="date"
                  value={formData.insuranceExpiry}
                  onChange={(e) => handleInputChange('insuranceExpiry', e.target.value)}
                  className={errors.insuranceExpiry ? 'border-red-500' : ''}
                />
                {errors.insuranceExpiry && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.insuranceExpiry}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="technicalReviewExpiry">Revisión Técnica</Label>
                <Input
                  id="technicalReviewExpiry"
                  type="date"
                  value={formData.technicalReviewExpiry}
                  onChange={(e) => handleInputChange('technicalReviewExpiry', e.target.value)}
                  className={errors.technicalReviewExpiry ? 'border-red-500' : ''}
                />
                {errors.technicalReviewExpiry && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {errors.technicalReviewExpiry}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-macartur-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (modalState.mode === 'edit' ? 'Actualizar' : 'Agregar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};